import { useEffect, useRef } from 'react';
import { ZONIFICACION_URBANA_PMTILES } from '../data/cordobaUrbanPmtiles';
import { ZONIFICACION_URBANA_LAYER_STYLE } from '../styles/mapTheme';
import {
  getSignedPmtilesUrl,
  refreshPmtilesVectorSourceIfNeeded,
} from '../../Utils/pmtilesAuth';
import { waitForMapStyleReady } from '../utils/waitForMapStyleReady';

const cfg = ZONIFICACION_URBANA_PMTILES;

const { fillFallbackColor, outlineColor, strokeWidth, strokeOpacity } =
  ZONIFICACION_URBANA_LAYER_STYLE;

const FILL_COLOR_EXPRESSION = ['coalesce', ['get', 'fill_color'], fillFallbackColor];

const LAYER_IDS = [cfg.fillLayerId, cfg.strokeLayerId];

function getInsertBeforeLayer(map) {
  const layers = map.getStyle()?.layers ?? [];
  const parcelLayer = layers.find((l) => l.id.startsWith('self-hosted-vector-layer-'));
  return parcelLayer?.id ?? undefined;
}

function applyVisibility(map, visible) {
  const vis = visible ? 'visible' : 'none';
  LAYER_IDS.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', vis);
    }
  });
}

function ensureSourceAndLayers(map, signedUrl, visible) {
  const visibility = visible ? 'visible' : 'none';
  const beforeId = getInsertBeforeLayer(map);

  if (!map.getSource(cfg.sourceId)) {
    map.addSource(cfg.sourceId, {
      type: 'vector',
      url: signedUrl,
    });
  }

  if (!map.getLayer(cfg.fillLayerId)) {
    map.addLayer(
      {
        id: cfg.fillLayerId,
        type: 'fill',
        source: cfg.sourceId,
        'source-layer': cfg.sourceLayer,
        minzoom: cfg.minZoom,
        maxzoom: cfg.maxZoom,
        layout: { visibility },
        paint: {
          'fill-color': FILL_COLOR_EXPRESSION,
          'fill-opacity': cfg.fillOpacity,
          'fill-outline-color': outlineColor,
        },
      },
      beforeId,
    );
  }

  if (!map.getLayer(cfg.strokeLayerId)) {
    map.addLayer(
      {
        id: cfg.strokeLayerId,
        type: 'line',
        source: cfg.sourceId,
        'source-layer': cfg.sourceLayer,
        maxzoom: cfg.maxZoom,
        layout: { visibility },
        paint: {
          'line-color': outlineColor,
          'line-width': strokeWidth,
          'line-opacity': strokeOpacity,
        },
      },
      beforeId,
    );
  }

  applyVisibility(map, visible);
}

/**
 * Capa de zonificación urbana — Provincia de Córdoba (IDECOR 406).
 * Colores desde `fill_color` en cada tile.
 *
 * @param {object} options
 * @param {React.MutableRefObject} options.mapInstanceRef
 * @param {boolean} [options.enabled=false]
 */
export function useZonificacionUrbanaLayer({ mapInstanceRef, enabled = false, mapAvailTick = 0 }) {
  const enabledRef = useRef(enabled);
  const layersReadyRef = useRef(false);
  const moveEndHandlerRef = useRef(null);
  const setupGenRef = useRef(0);

  enabledRef.current = enabled;

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !layersReadyRef.current) return;

    applyVisibility(map, enabled);

    if (!enabled && moveEndHandlerRef.current) {
      map.off('moveend', moveEndHandlerRef.current);
      moveEndHandlerRef.current = null;
    }
  }, [enabled, mapInstanceRef]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return undefined;

    if (!enabled) {
      if (layersReadyRef.current) {
        applyVisibility(map, false);
      }
      layersReadyRef.current = false;
      if (moveEndHandlerRef.current) {
        map.off('moveend', moveEndHandlerRef.current);
        moveEndHandlerRef.current = null;
      }
      return undefined;
    }

    const setupGen = setupGenRef.current + 1;
    setupGenRef.current = setupGen;
    let cancelled = false;

    const setup = async () => {
      await waitForMapStyleReady(map);
      if (cancelled || setupGenRef.current !== setupGen || !enabledRef.current) return;

      const signedUrl = await getSignedPmtilesUrl(cfg.pmtilesUrl);
      if (cancelled || setupGenRef.current !== setupGen || !enabledRef.current) return;
      if (!signedUrl) return;

      ensureSourceAndLayers(map, signedUrl, true);
      layersReadyRef.current = true;
      applyVisibility(map, true);

      if (cancelled || setupGenRef.current !== setupGen || !enabledRef.current) return;

      const onMoveEnd = () => {
        if (!enabledRef.current) return;

        void refreshPmtilesVectorSourceIfNeeded(map, cfg.sourceId, cfg.pmtilesUrl)
          .finally(() => {
            if (enabledRef.current) applyVisibility(map, true);
          });
      };

      if (moveEndHandlerRef.current) {
        map.off('moveend', moveEndHandlerRef.current);
      }
      moveEndHandlerRef.current = onMoveEnd;
      map.on('moveend', onMoveEnd);
    };

    void setup();

    return () => {
      cancelled = true;
      if (moveEndHandlerRef.current && map) {
        map.off('moveend', moveEndHandlerRef.current);
        moveEndHandlerRef.current = null;
      }
    };
  }, [enabled, mapInstanceRef, mapAvailTick]);
}
